import { Link } from "react-router";
import { ArrowRight, ChevronDown, HelpCircle } from "lucide-react";
import type { PortalFaqItem } from "@colab/shared";

type FaqAccordionProps = {
  items: PortalFaqItem[];
  /** Group items by category. Default: false (flat list). */
  groupByCategory?: boolean;
  /** Show a "View all FAQ" link to /portal/support. Hide on the Support page
   *  itself. Default: true. */
  showViewAll?: boolean;
  heading?: string;
};

// FAQ accordion built on the native <details> element — accessible without any
// client JS, keyboard-navigable by browser default. Markdown answers render as
// plain text with newline-preserved whitespace; a richer markdown renderer can
// land later if the answer corpus grows.
export function FaqAccordion({
  items,
  groupByCategory = false,
  showViewAll = true,
  heading = "Frequently asked questions",
}: FaqAccordionProps) {
  if (items.length === 0) {
    return (
      <section className="rounded-xl border bg-card p-6">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {heading}
          </h2>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          No FAQ entries yet. Once the partnerships team adds some, they&apos;ll appear here.
        </p>
      </section>
    );
  }

  const grouped = groupByCategory ? groupItems(items) : [{ category: null, items }];

  return (
    <section className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {heading}
          </h2>
        </div>
        {showViewAll ? (
          <Link
            to="/portal/support"
            className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            View all
            <ArrowRight className="h-3 w-3" />
          </Link>
        ) : null}
      </div>

      <div className="mt-4 space-y-6">
        {grouped.map((group) => (
          <div key={group.category ?? "_flat"}>
            {group.category ? (
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {group.category}
              </h3>
            ) : null}
            <ul className="divide-y rounded-lg border">
              {group.items.map((item) => (
                <li key={item.id}>
                  <details className="group">
                    <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm font-medium transition hover:bg-muted/40">
                      <span>{item.question}</span>
                      <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground transition group-open:rotate-180" />
                    </summary>
                    <div className="whitespace-pre-line border-t bg-muted/20 px-4 py-3 text-sm leading-relaxed text-foreground/90">
                      {item.answer_md}
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function groupItems(items: PortalFaqItem[]): { category: string; items: PortalFaqItem[] }[] {
  const map = new Map<string, PortalFaqItem[]>();
  for (const item of items) {
    const bucket = map.get(item.category) ?? [];
    bucket.push(item);
    map.set(item.category, bucket);
  }
  return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
}
